import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { FileText, Plus, ArrowRight, Globe, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const REPORT_TYPES = [
  { value: "climate_passport", label: "Climate Passport" },
  { value: "project_impact", label: "Project Impact Assessment" },
  { value: "technical", label: "Technical GHG Report" },
  { value: "corporate_summary", label: "Corporate Summary" },
  { value: "financier_summary", label: "Financier Summary" },
];

const REPORT_STATUS = {
  draft: { label: "Draft", class: "bg-slate-100 text-slate-600" },
  review_ready: { label: "Review Ready", class: "bg-amber-50 text-amber-700" },
  approved_for_issue: { label: "Approved", class: "bg-blue-50 text-blue-700" },
  issued: { label: "Issued", class: "bg-emerald-50 text-emerald-700" },
  superseded: { label: "Superseded", class: "bg-slate-50 text-slate-400" },
  withdrawn: { label: "Withdrawn", class: "bg-red-50 text-red-600" }
};

export default function Reports() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [form, setForm] = useState({ assessment_id: "", type: "climate_passport", title: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReports();
    loadAssessments();
  }, [currentOrg]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/reports${q}`);
      setReports(res.data);
    } catch {}
    setLoading(false);
  };

  const loadAssessments = async () => {
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/assessments${q}`);
      const complete = res.data.filter(a => ["analysis", "review", "approved", "issued"].includes(a.status));
      setAssessments(complete);
      if (complete.length > 0) setForm(f => ({ ...f, assessment_id: complete[0].id }));
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post("/reports", form);
      toast.success("Report created");
      setShowModal(false);
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create report"); }
    setSaving(false);
  };

  const handleStatusUpdate = async (reportId, status) => {
    try {
      await client.patch(`/reports/${reportId}/status`, { status });
      toast.success(`Report status updated to ${status}`);
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update status"); }
  };

  const handlePublish = async (reportId) => {
    try {
      await client.post("/registry/publish", { report_id: reportId, expiry_months: 12 });
      toast.success("Published to registry successfully!");
      setShowPublishModal(false);
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to publish to registry"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
          
      <PageHeader
        title="Reports"
        subtitle="Climate reports, passports, and assessment documents."
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Reports Available" value={reports.length} color="text-slate-900" />
      </div>
        <button onClick={() => setShowModal(true)} disabled={assessments.length === 0} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors disabled:opacity-50" data-testid="create-report-btn">
          <Plus size={14} /> New Report
        </button>
      </div>

      {assessments.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
          Complete at least one assessment with calculations before generating reports.
        </div>
      )}

      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="reports-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Report</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Created</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={5} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>)
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center">
                    <FileText size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">No reports yet</p>
                    <button onClick={() => setShowModal(true)} className="mt-2 text-xs text-[#059669] hover:underline" disabled={assessments.length === 0}>Generate your first report</button>
                  </td>
                </tr>
              ) : reports.map(r => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-900">{r.title}</p>
                    {r.reporting_year && <p className="text-xs text-slate-400">FY{r.reporting_year}</p>}
                  </td>
                  <td className="px-4 py-4 hidden sm:table-cell">
                    <span className="text-xs text-slate-500 capitalize">{r.type?.replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${REPORT_STATUS[r.status]?.class || "bg-slate-100 text-slate-600"}`}>
                      {REPORT_STATUS[r.status]?.label || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-400 hidden md:table-cell">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {r.status === "draft" && (
                        <button onClick={() => handleStatusUpdate(r.id, "review_ready")} className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded hover:bg-amber-100">Submit for Review</button>
                      )}
                      {r.status === "review_ready" && (
                        <button onClick={() => handleStatusUpdate(r.id, "approved_for_issue")} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">Approve</button>
                      )}
                      {r.status === "approved_for_issue" && (
                        <button onClick={() => handleStatusUpdate(r.id, "issued")} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Issue</button>
                      )}
                      {r.status === "issued" && (
                        <button onClick={() => { setSelectedReport(r); setShowPublishModal(true); }} className="text-xs px-2 py-1 bg-[#059669] text-white rounded hover:bg-emerald-700 flex items-center gap-1">
                          <Globe size={11} /> Publish
                        </button>
                      )}
                      {r.type === "climate_passport" && (
                        <button onClick={() => navigate(`/passport/${r.assessment_id}`)} className="text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50">
                          View Passport
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </SectionCard>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Generate Report</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Assessment</label>
              <select value={form.assessment_id} onChange={e => setForm(f => ({ ...f, assessment_id: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]" required data-testid="report-assessment-select">
                <option value="">Select assessment</option>
                {assessments.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Report Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="report-type-select">
                {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Report Title</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="Leave blank to auto-generate" data-testid="report-title-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-10 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="create-report-submit-btn">
                {saving ? "Creating..." : "Generate Report"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Publish Confirm Modal */}
      <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Publish to Registry</DialogTitle></DialogHeader>
          <div className="mt-3 space-y-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm font-medium text-slate-900">{selectedReport?.title}</p>
              <p className="text-xs text-slate-400 mt-1">This report will be published to the public registry with a 12-month validity period.</p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <CheckCircle2 size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">Once published, the registry record becomes publicly accessible and immutable. Ensure the report is accurate before publishing.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowPublishModal(false)} className="flex-1 h-10 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => handlePublish(selectedReport?.id)} className="flex-1 h-10 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2" data-testid="confirm-publish-btn">
                <Globe size={14} /> Publish to Registry
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
